'use client';

import { usePathname } from 'next/navigation';
import { useEffect } from 'react';

const NICEJOB_SDK_SELECTOR =
    'script[src*="cdn.nicejob.co/js/sdk"], script[src*="cdn.nicejob.co/sdk"]';
const NICEJOB_WIDGET_SELECTOR = [
    '.nj-lead',
    '.h-lead',
    '.nj-stories',
    '.nj-story',
    '.nicejob-stories',
    '.nj-featured-story',
    '.nj-recommendation',
    '.nj-recommend',
    '.nj-review',
    '.n-review',
    '.nj-badge',
    '.nicejob-badge',
    '.nj-social-proof',
    '.nj-engage',
    '.nj-carousel',
    '.nj-referral-lead-form',
    '.nj-nps-feedback-routing',
    '.nj-nps-survey',
    '.nj-referral-share',
]
    .map((selector) => `${selector}:not([data-nj-element-handled])`)
    .join(', ');

const RETRY_INTERVAL_MS = 250;
const INSTALL_TIMEOUT_MS = 5000;

export function hasUnhandledNiceJobWidgets(root = document) {
    return Boolean(root?.querySelector?.(NICEJOB_WIDGET_SELECTOR));
}

function getReadyNiceJobSdk() {
    const script = document.querySelector(NICEJOB_SDK_SELECTOR);
    if (
        !script?.hasAttribute('data-csrf') ||
        typeof window.NiceJobSDKv2 !== 'function'
    ) {
        return null;
    }

    return window.NiceJobSDKv2;
}

/**
 * NiceJob scans the document once when its SDK loads. React hydration and
 * client-side navigation can replace or add CTA elements after that scan,
 * leaving their fallback href="#" active. Re-run the SDK only when an
 * unhandled widget is present and the first SDK initialization has completed.
 */
export default function NiceJobInitializer() {
    const pathname = usePathname();

    useEffect(() => {
        let disposed = false;
        let retryTimer = null;
        let installStartedAt = 0;

        const clearRetry = () => {
            if (retryTimer !== null) {
                window.clearTimeout(retryTimer);
                retryTimer = null;
            }
        };

        const schedule = (callback, delay = RETRY_INTERVAL_MS) => {
            clearRetry();
            retryTimer = window.setTimeout(callback, delay);
        };

        const ensureWidgetsInstalled = () => {
            retryTimer = null;
            if (disposed) return;
            if (!hasUnhandledNiceJobWidgets()) {
                installStartedAt = 0;
                return;
            }

            if (installStartedAt) {
                if (Date.now() - installStartedAt < INSTALL_TIMEOUT_MS) {
                    schedule(ensureWidgetsInstalled);
                    return;
                }
                installStartedAt = 0;
            }

            const initialize = getReadyNiceJobSdk();
            if (!initialize) return;

            installStartedAt = Date.now();
            try {
                initialize();
            } catch {
                installStartedAt = 0;
            }
            schedule(ensureWidgetsInstalled);
        };

        const observer = new MutationObserver(() => {
            if (!installStartedAt && hasUnhandledNiceJobWidgets()) {
                schedule(ensureWidgetsInstalled, 0);
            }
        });

        if (document.documentElement) {
            observer.observe(document.documentElement, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['data-csrf'],
            });
        }

        schedule(ensureWidgetsInstalled, 0);

        return () => {
            disposed = true;
            clearRetry();
            observer.disconnect();
        };
    }, [pathname]);

    return null;
}
